/* Two evaluation runs, differenced.
 *
 * A `TrendPoint` is a run reduced to what a chart needs — a score id, an instant, a value, a
 * coverage and a model version. The decomposition lives on the `Score` behind it, so a comparison
 * is **exactly two reads**, and that bound is what makes this affordable where drawing per-dimension
 * history for a whole window is not.
 *
 * The run list is the table view of the trend line above it: same points, same order, and a reader
 * who cannot read a chart can read this instead. Selecting a row makes it the newer side, which
 * demotes the previous newer side to the older one — the gesture somebody actually makes is "and
 * how does it compare to *this* one", not "please set slot B".
 */

import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { UnavailableState } from '@/shared/ui/States';

import { useScore } from '../api/scoring';
import type { TrendPoint } from '../api/scoring';
import { compareScores } from '../model/compare';
import { formatScore, stalenessAge } from '../model/presentation';

export interface RunComparisonProps {
  /** Oldest first, as the trend returns them. */
  readonly points: readonly TrendPoint[];
  readonly newerId: string | undefined;
  readonly olderId: string | undefined;
  readonly onPick: (scoreId: string) => void;
  readonly onSetOlder: (scoreId: string) => void;
}

function when(instant: string): string {
  const age = stalenessAge(instant, Date.now());
  if (age === null) return instant;
  return `${String(age.count)} ${age.unit}`;
}

export function RunComparison({
  points,
  newerId,
  olderId,
  onPick,
  onSetOlder,
}: RunComparisonProps): ReactElement {
  const { t } = useTranslation();

  /* Newest first for the list and the selects: the run somebody wants is almost always the last
     one, and making them scroll past ninety to reach it is the sort of thing that gets a screen
     called unusable. The trend's own order is oldest-first, which is right for a chart. */
  const runs = useMemo(() => [...points].reverse(), [points]);

  const newer = useScore(newerId);
  const older = useScore(olderId);

  if (points.length < 2) {
    return (
      <EmptyState
        title={t('scoring.runs.tooFew')}
        guidance={t('scoring.runs.tooFewGuidance', { count: points.length })}
      />
    );
  }

  const diff =
    newer.data !== undefined && older.data !== undefined
      ? compareScores(newer.data, older.data)
      : null;

  return (
    <div className="lq-stack">
      <div className="lq-cmp__pickers">
        <label className="lq-field lq-field--inline">
          <span className="lq-field__label">{t('scoring.runs.newer')}</span>
          <select
            className="lq-input"
            value={newerId ?? ''}
            onChange={(event) => {
              onPick(event.target.value);
            }}
          >
            {runs.map((point) => (
              <option key={point.score_id} value={point.score_id}>
                {t('scoring.runs.option', {
                  age: when(point.computed_at),
                  value: point.value === null ? '—' : formatScore(point.value),
                  model: point.score_model_version,
                })}
              </option>
            ))}
          </select>
        </label>
        <span className="lq-cmp__vs">{t('scoring.runs.versus')}</span>
        <label className="lq-field lq-field--inline">
          <span className="lq-field__label">{t('scoring.runs.older')}</span>
          <select
            className="lq-input"
            value={olderId ?? ''}
            onChange={(event) => {
              onSetOlder(event.target.value);
            }}
          >
            {runs.map((point) => (
              <option key={point.score_id} value={point.score_id}>
                {t('scoring.runs.option', {
                  age: when(point.computed_at),
                  value: point.value === null ? '—' : formatScore(point.value),
                  model: point.score_model_version,
                })}
              </option>
            ))}
          </select>
        </label>
      </div>

      {newer.isError || older.isError ? (
        <UnavailableState
          title={t('scoring.runs.unreadable')}
          guidance={
            (newer.error ?? older.error)?.message ?? t('scoring.runs.unreadableGuidance')
          }
        />
      ) : diff === null ? (
        <SkeletonRows rows={4} columns={4} />
      ) : (
        <>
          <Alert
            tone={diff.samePlan && diff.comparable ? 'notice' : 'warning'}
            title={
              !diff.comparable
                ? t('scoring.runs.modelDiffersTitle')
                : diff.samePlan
                  ? t('scoring.change.samePlanTitle')
                  : t('scoring.change.differentPlanTitle')
            }
          >
            <p className="lq-nomargin">
              {!diff.comparable
                ? t('scoring.runs.modelDiffersBody', {
                    before: diff.modelBefore,
                    after: diff.modelNow,
                  })
                : diff.samePlan
                  ? t('scoring.change.samePlanBody', { hash: diff.planNow })
                  : t('scoring.change.differentPlanBody', {
                      before: diff.planBefore,
                      after: diff.planNow,
                      added: diff.rulesAdded,
                      removed: diff.rulesRemoved,
                      reVersioned: diff.rulesReVersioned,
                    })}
            </p>
          </Alert>

          <dl className="lq-figures">
            <div>
              <dt>{t('scoring.runs.overall')}</dt>
              <dd>
                {diff.overallBefore === null ? '—' : formatScore(diff.overallBefore)}
                {' → '}
                {diff.overallNow === null ? '—' : formatScore(diff.overallNow)}
                {diff.overallDelta !== null && diff.overallDelta !== 0 && (
                  <span
                    className={
                      diff.overallDelta > 0
                        ? 'lq-delta lq-delta--up'
                        : 'lq-delta lq-delta--down'
                    }
                  >
                    {' '}
                    <span aria-hidden="true">
                      {diff.overallDelta > 0 ? '▲' : '▼'}
                    </span>{' '}
                    {formatScore(Math.abs(diff.overallDelta))}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt>{t('scoring.change.newFailuresLabel')}</dt>
              <dd className="lq-delta--down">{diff.newFailures.toLocaleString()}</dd>
            </div>
            <div>
              <dt>{t('scoring.change.resolvedLabel')}</dt>
              <dd className="lq-delta--up">{diff.resolvedFailures.toLocaleString()}</dd>
            </div>
            <div>
              <dt>{t('scoring.runs.rulesChanged')}</dt>
              <dd>
                {t('scoring.runs.rulesChangedValue', {
                  added: diff.rulesAdded,
                  removed: diff.rulesRemoved,
                  reVersioned: diff.rulesReVersioned,
                })}
              </dd>
            </div>
          </dl>

          <section>
            <h3 className="lq-drawer__h">{t('scoring.runs.dimensionDiff')}</h3>
            <table className="lq-table lq-table--compact">
              <caption className="lq-visually-hidden">
                {t('scoring.runs.dimensionCaption')}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('scoring.decomposition.dimension')}</th>
                  <th scope="col" className="lq-table__num">
                    {t('scoring.runs.before')}
                  </th>
                  <th scope="col" className="lq-table__num">
                    {t('scoring.runs.after')}
                  </th>
                  <th scope="col" className="lq-table__num">
                    {t('scoring.runs.change')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {diff.dimensions.map((move) => (
                  <tr key={move.dimension}>
                    <td>{t(`scoring.dimension.${move.dimension}`)}</td>
                    <td className="lq-table__num lq-muted">
                      {move.before === null ? '—' : formatScore(move.before)}
                    </td>
                    <td className="lq-table__num">
                      {move.now === null ? '—' : formatScore(move.now)}
                    </td>
                    <td className="lq-table__num">
                      {move.delta === null ? (
                        <span className="lq-muted">{t('scoring.runs.noComparison')}</span>
                      ) : Math.abs(move.delta) < 0.1 ? (
                        <span className="lq-muted">{t('scoring.delta.flat')}</span>
                      ) : (
                        <span
                          className={
                            move.delta > 0
                              ? 'lq-delta lq-delta--up'
                              : 'lq-delta lq-delta--down'
                          }
                        >
                          <span aria-hidden="true">{move.delta > 0 ? '▲' : '▼'}</span>{' '}
                          {formatScore(Math.abs(move.delta))}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section>
            <h3 className="lq-drawer__h">{t('scoring.runs.ruleDiff')}</h3>
            <table className="lq-table lq-table--compact">
              <caption className="lq-visually-hidden">
                {t('scoring.runs.ruleCaption')}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('scoring.decomposition.column.rule')}</th>
                  <th scope="col">{t('scoring.runs.change')}</th>
                  <th scope="col" className="lq-table__num">
                    {t('scoring.decomposition.column.pass')}
                  </th>
                  <th scope="col" className="lq-table__num">
                    {t('scoring.runs.newFailuresShort')}
                  </th>
                  <th scope="col" className="lq-table__num">
                    {t('scoring.runs.resolvedShort')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {diff.rules.map((rule) => (
                  <tr key={rule.ruleName}>
                    <td>
                      <span className="lq-mono">{rule.ruleName}</span>
                      <span className="lq-scores__sub lq-mono">
                        {rule.labelChanged
                          ? t('scoring.runs.labelMoved', {
                              before: rule.previousLabel ?? '—',
                              after: rule.ruleLabel,
                            })
                          : rule.ruleLabel}
                      </span>
                    </td>
                    <td>{t(`scoring.runs.change.${rule.change}`)}</td>
                    <td className="lq-table__num">
                      {rule.passBefore === null
                        ? '—'
                        : `${(rule.passBefore * 100).toFixed(1)}%`}
                      {' → '}
                      {rule.passNow === null
                        ? '—'
                        : `${(rule.passNow * 100).toFixed(1)}%`}
                    </td>
                    <td className="lq-table__num">
                      {rule.newFailures === 0 ? '—' : rule.newFailures.toLocaleString()}
                    </td>
                    <td className="lq-table__num">
                      {rule.resolvedFailures === 0
                        ? '—'
                        : rule.resolvedFailures.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      <section>
        <h3 className="lq-drawer__h">{t('scoring.runs.historyHead')}</h3>
        <table className="lq-table lq-table--compact">
          <caption className="lq-visually-hidden">
            {t('scoring.runs.historyCaption')}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('scoring.runs.when')}</th>
              <th scope="col" className="lq-table__num">
                {t('scoring.listing.column.score')}
              </th>
              <th scope="col" className="lq-table__num">
                {t('scoring.listing.column.coverage')}
              </th>
              <th scope="col">{t('scoring.runs.model')}</th>
              <th scope="col">{t('scoring.runs.compare')}</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((point) => {
              const role =
                point.score_id === newerId
                  ? t('scoring.runs.roleNewer')
                  : point.score_id === olderId
                    ? t('scoring.runs.roleOlder')
                    : null;
              return (
                <tr
                  key={point.score_id}
                  className={role === null ? undefined : 'lq-row--chosen'}
                >
                  <td>
                    {t('scoring.evaluated.ago', { age: when(point.computed_at) })}
                    {!point.evidence_complete && (
                      <span
                        className="lq-scores__sub"
                        title={t('scoring.score.partialMeaning')}
                      >
                        {t('scoring.score.partial')}
                      </span>
                    )}
                  </td>
                  <td className="lq-table__num lq-mono">
                    {point.value === null ? '—' : formatScore(point.value)}
                  </td>
                  <td className="lq-table__num lq-mono">
                    {point.column_coverage === null
                      ? '—'
                      : `${String(Math.round(point.column_coverage * 100))}%`}
                  </td>
                  <td className="lq-mono">
                    {t('scoring.model.version', { version: point.score_model_version })}
                  </td>
                  <td>
                    {role ?? (
                      <button
                        type="button"
                        className="lq-linkish"
                        onClick={() => {
                          onPick(point.score_id);
                        }}
                      >
                        {t('scoring.runs.setNewer')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
